// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useState, useEffect, useContext } from "react";
import "./App.css";
import { TeamsUserCredential } from "@microsoft/teamsfx";
import { Configuration, EmpNotesApi, EnsUserNoteTypeTid, UserInfoApi } from "../clients/ensusers";
import { TeamsFxContext } from "../internal/context";
import { Button, DatePicker, Form, Input, Spin, Typography, message } from "antd";
import { Store } from "antd/es/form/interface";

const { Title } = Typography;

type GeneratedClientClass<T> = {
  new(config: any): T;
};

type GeneratedClientClassConfiguration<C> = {
  new(configParams: GeneratedClientClassConfigurationParamters): C;
};

type GeneratedClientClassConfigurationParamters = {
  apiKey?: string | ((name: string) => string);
};

const authenticatedClient = async <T, C>(
  teamsUserCredential: TeamsUserCredential,
  Client: GeneratedClientClass<T>,
  Config: GeneratedClientClassConfiguration<C>
): Promise<T> => {
  const scopes = [`api://b3733c43-473f-499c-8b7d-b1310487b918/access_as_user`];
  try {
    await teamsUserCredential.login(scopes);
    var accessTokenReq = await teamsUserCredential.getToken(scopes);
    var accessToken = accessTokenReq?.token;
    return new Client(new Config({ apiKey: `Bearer ${accessToken}` }));
  } catch (ex: any) {
    throw new Error("Failed to create authenticated Ensurem API client." + ex);
  }
}

export default function Tab() {
  const { teamsUserCredential } = useContext(TeamsFxContext);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [selectedDate, setSelectedDate] = useState<Date>();

  useEffect(() => {
    setLoadingAuth(teamsUserCredential == undefined)

    if (teamsUserCredential != undefined)
      messageApi.info(JSON.stringify(teamsUserCredential));
  }, [teamsUserCredential]);

  // early out rendering this component
  if (loadingAuth)
    return <>
      {contextHolder}
      Logging in...
    </>

  const getEnsUserInfo = async () => {
    if (!teamsUserCredential)
      return;

    var userEmail = (await teamsUserCredential.getUserInfo()).preferredUserName;
    const client = await authenticatedClient(teamsUserCredential, UserInfoApi, Configuration);
    return await client.userInfoGetBasicInfo({ userEmail: userEmail });
  }

  const handleFormSubmit = async (values: Store) => {
    if (!teamsUserCredential)
      return;

    setLoading(true);
    try {
      var userInfo = await getEnsUserInfo();

      var client = await authenticatedClient(teamsUserCredential, EmpNotesApi, Configuration);
      await client.empNotesInsertNewEmpNote({
        empCreateNoteRequest: {
          authorEnsUserTid: userInfo?.ensUserTid ?? -1,
          subjectEnsUserTid: userInfo?.ensUserTid ?? -1,
          ensUserNoteTypeTid: EnsUserNoteTypeTid.AbsenceRequest,
          effectiveDate: selectedDate,
          noteContent: `${values.comment}`,
          hiddenNoteContent: null,
          noteAttachments: null
        }
      });

      messageApi.success("Absence request submitted successfully.");
      form.resetFields();
    } catch (error) {
      console.error("Failed to submit absence request:", error);
      messageApi.error("Failed to submit absence request. Please try again." + error);
    } finally {
      setLoading(false); // Set loading state back to false
    }
  };

  return (
    <>
      {contextHolder}      
      <Title level={4} style={{ marginTop: 0 }}>Request Absence</Title>
      <Spin spinning={loading || loadingAuth}>
        <Form form={form} onFinish={handleFormSubmit}>
          <Form.Item
            name="date"
            rules={[
              { required: true, message: "Please select a date." },
            ]}
          >
            <DatePicker
              onChange={(date) => setSelectedDate(date?.toDate())}
              placeholder="Select Date"
              style={{ width: "15%" }}
            />
          </Form.Item>
          <Form.Item
            name="comment"
            rules={[
              { required: true, message: "Please enter your absence request comment." },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Enter your absence request comment" style={{ width: "50%" }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </>
  );
}
